import { create } from "zustand";
import { IAlertState } from "../interface/alert-state.interface";
import { initAlertState } from "../data/initialize/alert-states.data";
import { IFileDataBase64 } from "../interface/fileDataBase64.interface";
import OwnershipCarType from "../types/ownershipCar.type";

export interface IRenewedPermitFormValues {
    citizenComment: string,
    scanPersonId: IFileDataBase64 | null,
    scanCarId: IFileDataBase64 | null,
    scanWorkStatment: IFileDataBase64 | null,
    cardNumber: string,
    cardExpireDateMM: string,
    cardExpireDateYY: string,
    cardAddedNumber: string,
    cardHolderId: string,
    cardHolderName: string,
}

export type RenewedPermitFormValuesType = keyof IRenewedPermitFormValues;
export type RenewedPermitFormErrorType = { [key in RenewedPermitFormValuesType]?: string };
export type RenewedPermitFormTouchedType = { [key in RenewedPermitFormValuesType]?: boolean };
export type RenewedPermitStepType = 'confirmation' | 'form';

type OpenRenewedPermitModalPayload = {
    citizenComment: string,
    endValidityDate: string;
    permitId: number;
    isCompany: number,
    permitFee: number,
    personId: string;
    vehicleNum: string;
    otpCode: string;
}

export interface IRenewedPermitForm {
    isValid: boolean,
    values: IRenewedPermitFormValues,
    errors: RenewedPermitFormErrorType,
    touched: RenewedPermitFormTouchedType,
}

const initRenewedPermitForm: IRenewedPermitForm = {
    isValid: false,
    values: {
        citizenComment: '',
        scanPersonId: null,
        scanCarId: null,
        scanWorkStatment: null,
        cardNumber: '',
        cardExpireDateMM: '',
        cardExpireDateYY: '',
        cardAddedNumber: '',
        cardHolderId: '',
        cardHolderName: '',
    },
    errors: {},
    touched: {},
}

export interface IPaymentProcessResult {
    paymentMethod: string,
    paymentAmount: number,
    hasReceipt: boolean,
    asmachta: string,
    receiptUrl: string,
}

export interface IRenewedPermitConfirmation {
    requestId: number,
    paymentProcessResult: IPaymentProcessResult,
}

export interface IModalRenewedPermitData {
    isModalOpen: boolean;
    personId: string;
    vehicleNum: string;
    otpCode: string;
    endValidityDate: string;
    permitId: number;
    isCompany: number,
    permitFee: number,
    renewedPermitStep: 'confirmation' | 'form';
    alertState: IAlertState;
    form: IRenewedPermitForm;
    renewedPermitConfirmation: IRenewedPermitConfirmation;
}

const initConfirmationData: IRenewedPermitConfirmation = {
    requestId: -1,
    paymentProcessResult: {
        paymentMethod: '',
        paymentAmount: 0,
        hasReceipt: false,
        asmachta: '',
        receiptUrl: '',
    }
}

const initModalData: IModalRenewedPermitData = {
    isModalOpen: false,
    renewedPermitStep: 'form',
    endValidityDate: '',
    permitId: -1,
    personId: '',
    vehicleNum: '',
    otpCode: '',
    isCompany: OwnershipCarType.PRIVATE,
    permitFee: 0,
    alertState: { ...initAlertState },
    form: { ...initRenewedPermitForm },
    renewedPermitConfirmation: { ...initConfirmationData },
};


interface IRenewedPermitModalStore {
    data: IModalRenewedPermitData;
    setModalRenewedPermitData: (modalRenewedPermitData: IModalRenewedPermitData) => void;
    resetModalRenewedPermitData: () => void;
    openModalRenewedPermit: (payload: OpenRenewedPermitModalPayload) => void;
    closeModalRenewedPermit: () => void;
    setModalRenewedPermitAlertState: (alertState: IAlertState) => void;
    setModalRenewedPermitStep: (stage: RenewedPermitStepType) => void;
    moveToRenewedPermitStep: (renewedPermitConfirmation: IRenewedPermitConfirmation) => void;
    setFormBlurr: (field: RenewedPermitFormValuesType) => void;
    setModalRenewedPermitValueForm: (field: RenewedPermitFormValuesType, value: any) => void;
    markAllFieldsAsTouched: () => void;
    setModalRenewedPermitFormError: (errors: RenewedPermitFormErrorType) => void;
}

export const useRenewedPermitModalStore = create<IRenewedPermitModalStore>()((set) => ({
    data: { ...initModalData },
    setModalRenewedPermitData: (modalRenewedPermitData: IModalRenewedPermitData) => set((state) => ({
        ...state,
        modalRenewedPermitData: modalRenewedPermitData,
    })),
    resetModalRenewedPermitData: () => set((state) => ({
        ...state,
        data: { ...initModalData },
    })),
    openModalRenewedPermit: (payload: OpenRenewedPermitModalPayload) => set((state) => ({
        ...state,
        data: {
            ...state.data,
            isModalOpen: true,
            endValidityDate: payload.endValidityDate,
            permitId: payload.permitId,
            isCompany: payload.isCompany,
            permitFee: payload.permitFee,
            personId: payload.personId,
            vehicleNum: payload.vehicleNum,
            otpCode: payload.otpCode,
            form: {
                ...state.data.form,
                values: {
                    ...state.data.form.values,
                    citizenComment: payload.citizenComment,
                }
            }
        },
    })),
    closeModalRenewedPermit: () => set((state) => ({
        ...state,
        data: {
            ...state.data,
            isModalOpen: false,
        },
    })),
    setModalRenewedPermitAlertState: (alertState: IAlertState) => set((state) => ({
        ...state,
        data: {
            ...state.data,
            alertState: alertState,
        },
    })),
    setModalRenewedPermitStep: (stage: RenewedPermitStepType) => set((state) => ({
        ...state,
        data: {
            ...state.data,
            renewedPermitStep: stage,
        },
    })),
    moveToRenewedPermitStep: (renewedPermitConfirmation: IRenewedPermitConfirmation) => set((state) => ({
        ...state,
        data: {
            ...state.data,
            renewedPermitStep: 'confirmation',
            renewedPermitConfirmation: { ...renewedPermitConfirmation },
        },
    })),
    setFormBlurr: (field: RenewedPermitFormValuesType) => set((state) => ({
        ...state,
        data: {
            ...state.data,
            form: {
                ...state.data.form,
                touched: {
                    ...state.data.form.touched,
                    [field]: true,
                },
            },
        },
    })),
    setModalRenewedPermitValueForm: (field: RenewedPermitFormValuesType, value: any) => set((state) => ({
        ...state,
        data: {
            ...state.data,
            form: {
                ...state.data.form,
                values: {
                    ...state.data.form.values,
                    [field]: value,
                },
            },
        },
    })),
    markAllFieldsAsTouched: () => set((state) => {

        const allFieldsTouched: RenewedPermitFormTouchedType = {
            citizenComment: true,
            scanPersonId: true,
            scanCarId: true,
            scanWorkStatment: true,
            cardNumber: true,
            cardExpireDateMM: true,
            cardExpireDateYY: true,
            cardAddedNumber: true,
            cardHolderId: true,
            cardHolderName: true,
        };

        return {
            ...state,
            data: {
                ...state.data,
                form: {
                    ...state.data.form,
                    touched: { ...allFieldsTouched },
                },
            },
        };
    }),
    setModalRenewedPermitFormError: (errors: RenewedPermitFormErrorType) => set((state) => ({
        ...state,
        data: {
            ...state.data,
            form: {
                ...state.data.form,
                errors: {
                    ...errors,
                },
            },
        },
    }))
}));

export const useSetModalRenewedPermitFormError = () => {
    return useRenewedPermitModalStore((state) => state.setModalRenewedPermitFormError);
}

export const useMarkAllRenewedPermitFormFieldsAsTouched = () => {
    return useRenewedPermitModalStore((state) => state.markAllFieldsAsTouched);
}

export const useSetModalRenewedPermitValueForm = () => {
    return useRenewedPermitModalStore((state) => state.setModalRenewedPermitValueForm);
}

export const useSetModalRenewedPermitFormBlurAndError = () => {
    return useRenewedPermitModalStore((state) => state.setFormBlurr);
}

export const useMoveToRenewedPermitStep = () => {
    return useRenewedPermitModalStore((state) => state.moveToRenewedPermitStep);
}

export const useSetModalRenewedPermitStep = () => {
    return useRenewedPermitModalStore((state) => state.setModalRenewedPermitStep);
}

export const useSetModalRenewedPermitAlertState = () => {
    return useRenewedPermitModalStore((state) => state.setModalRenewedPermitAlertState);
}

export const useResetModalRenewedPermitData = () => {
    return useRenewedPermitModalStore((state) => state.resetModalRenewedPermitData);
}

export const useSetModalRenewedPermitData = () => {
    return useRenewedPermitModalStore((state) => state.setModalRenewedPermitData);
}

export const useModalRenewedPermitData = () => {
    return useRenewedPermitModalStore((state) => state.data);
}

export const useIsModalRenewedOpenPermit = () => {
    return useRenewedPermitModalStore((state) => state.data.isModalOpen);
}

export const useGetModalRenewedAlertState = () => {
    return useRenewedPermitModalStore((state) => state.data.alertState);
}

export const useOpenModalRenewedPermit = () => {
    return useRenewedPermitModalStore((state) => state.openModalRenewedPermit);
}

export const useCloseModalRenewedPermit = () => {
    return useRenewedPermitModalStore((state) => state.closeModalRenewedPermit);
}